import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const TutorialPage = () => {
  const [activeSection, setActiveSection] = useState(null);
  const [showTOC, setShowTOC] = useState(false);
  const navigate = useNavigate();

  // Check authentication status
  const isLoggedIn = () => {
    return localStorage.getItem('token') !== null;
  };

  // Conditional navigation handler
  const handleResearchPageClick = (e) => {
    e.preventDefault();
    if (isLoggedIn()) {
      navigate('/research');
    } else {
      navigate('/login');
    }
  };

  // Navigation handler for Resources page (where PDF download is located)
  const handleResourcesPageClick = (e) => {
    e.preventDefault();
    navigate('/resources');
  };

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setShowTOC(false);
    }
  };

  const TutorialSection = ({ id, title, icon, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    
    return (
      <div id={id} className="tutorial-section" style={{
        background: 'white',
        borderRadius: '10px',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="tutorial-section-button"
          style={{
            width: '100%',
            padding: '1.5rem',
            background: 'none',
            border: 'none',
            textAlign: 'left',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '1.2rem',
            fontWeight: '600',
            color: '#2e7d32',
            transition: 'background 0.3s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#f8f8f8'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
        >
          <span className="tutorial-section-title" style={{display: 'flex', alignItems: 'center', flex: 1}}>
            <span className="tutorial-section-icon" style={{fontSize: '1.5rem', marginRight: '0.75rem', flexShrink: 0}}>{icon}</span>
            <span className="tutorial-section-text">{title}</span>
          </span>
          <span className="tutorial-section-arrow" style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s',
            flexShrink: 0,
            marginLeft: '0.5rem'
          }}>
            ▼
          </span>
        </button>
        {isOpen && (
          <div className="tutorial-section-content" style={{
            padding: '0 1.5rem 1.5rem',
            borderTop: '1px solid #f0f0f0',
            animation: 'slideDown 0.3s ease-out'
          }}>
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="tutorial-container" style={{maxWidth: '1000px', margin: '0 auto', padding: '2rem'}}>
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .quick-start-box {
          background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
          border: 2px solid #2e7d32;
          border-radius: 10px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }
        .try-this-box {
          background: #fff9c4;
          border-left: 4px solid #f9a825;
          padding: 1rem 1.5rem;
          margin: 1rem 0;
          border-radius: 0 5px 5px 0;
        }
        .success-story-box {
          background: #e3f2fd;
          border-left: 4px solid #1976d2;
          padding: 1rem 1.5rem;
          margin: 1rem 0;
          border-radius: 0 5px 5px 0;
          font-style: italic;
        }
        .chunk-box {
          background: #f3e5f5;
          border: 2px solid #7b1fa2;
          border-radius: 8px;
          padding: 1.5rem;
          margin: 1rem 0;
        }
        .book-section-box {
          background: linear-gradient(135deg, #fff3e0 0%, #fce4ec 50%, #f3e5f5 100%);
          border: 2px solid #7b1fa2;
          border-radius: 12px;
          padding: 2rem;
          margin: 1.5rem 0;
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        .workflow-box {
          background: #e8f5e9;
          border-left: 4px solid #2e7d32;
          padding: 1.5rem;
          margin: 1rem 0;
          border-radius: 0 8px 8px 0;
        }
        .gnology-journal-box {
          background: linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 50%, #f3e5f5 100%);
          border: 3px solid #2e7d32;
          border-radius: 12px;
          padding: 2rem;
          margin: 1.5rem 0;
          box-shadow: 0 6px 12px rgba(0,0,0,0.15);
        }
        .pdf-download-box {
          background: linear-gradient(135deg, #1565c0 0%, #1976d2 100%);
          color: white;
          border-radius: 12px;
          padding: 2rem;
          margin: 1.5rem 0;
          box-shadow: 0 6px 12px rgba(21, 101, 192, 0.3);
          text-align: center;
        }
        .journal-feature-box {
          background: #f8f9fa;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 1.5rem;
          margin: 1rem 0;
        }
        .tutorial-part {
          background: #f8f9fa;
          border-left: 4px solid #2e7d32;
          padding: 1.5rem;
          margin: 1.5rem 0;
          border-radius: 0 8px 8px 0;
        }
        .forensic-hero-box {
          background: linear-gradient(135deg, #d32f2f 0%, #1565c0 50%, #2e7d32 100%);
          color: white;
          border-radius: 12px;
          padding: 2rem;
          margin: 1.5rem 0;
          box-shadow: 0 6px 12px rgba(0,0,0,0.2);
          text-align: center;
        }
        .forensic-comparison-box {
          background: #f8f9fa;
          border: 2px solid #d32f2f;
          border-radius: 8px;
          padding: 1.5rem;
          margin: 1rem 0;
        }
        .forensic-tutorial-box {
          background: linear-gradient(135deg, #fff3e0 0%, #ffebee 100%);
          border: 2px solid #d32f2f;
          border-radius: 10px;
          padding: 1.5rem;
          margin: 1rem 0;
        }
        .forensic-case-study-box {
          background: #e8eaf6;
          border-left: 4px solid #3f51b5;
          padding: 1.5rem;
          margin: 1rem 0;
          border-radius: 0 8px 8px 0;
        }
        .forensic-feature-box {
          background: #f1f8e9;
          border: 1px solid #4caf50;
          border-radius: 8px;
          padding: 1.5rem;
          margin: 1rem 0;
        }
        .toc-button {
          position: fixed;
          bottom: 2rem;
          right: 2rem;
          background: #2e7d32;
          color: white;
          border: none;
          border-radius: 50%;
          width: 60px;
          height: 60px;
          font-size: 1.5rem;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          z-index: 100;
          transition: transform 0.3s;
        }
        .toc-button:hover {
          transform: scale(1.1);
        }
        .toc-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 200;
          animation: fadeIn 0.3s;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .toc-content {
          background: white;
          border-radius: 10px;
          padding: 2rem;
          max-width: 500px;
          width: 90%;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
        }
        .toc-item {
          padding: 0.75rem 1rem;
          margin: 0.25rem 0;
          border-radius: 5px;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
        }
        .toc-item:hover {
          background: #e8f5e9;
          transform: translateX(5px);
        }
        
        /* Enhanced Mobile Responsive Styles with Maximum Specificity */
        @media (max-width: 768px) {
          /* Enhanced specificity for mobile container */
          body .tutorial-container,
          .tutorial-container {
            padding: 1rem !important;
            padding-bottom: 0.5rem !important;
            margin-bottom: 0 !important;
          }
          
          /* Force footer consistency */
          body .tutorial-container + *,
          .tutorial-container + * {
            margin-top: 0 !important;
          }
          
          /* Tutorial section button fixes */
          body .tutorial-section-button,
          .tutorial-section-button {
            font-size: 0.95rem !important;
            padding: 1rem !important;
            line-height: 1.4;
          }
          
          /* Make titles wrap properly */
          body .tutorial-section-title,
          .tutorial-section-title {
            flex-wrap: wrap;
            align-items: flex-start !important;
          }
          
          body .tutorial-section-text,
          .tutorial-section-text {
            word-break: break-word;
            hyphens: auto;
            line-height: 1.3;
          }
          
          /* Smaller icons on mobile */
          body .tutorial-section-icon,
          .tutorial-section-icon {
            font-size: 1.2rem !important;
            margin-right: 0.5rem !important;
          }
          
          /* Arrow adjustment */
          body .tutorial-section-arrow,
          .tutorial-section-arrow {
            font-size: 0.8rem !important;
          }
          
          /* Content padding adjustment */
          body .tutorial-section-content,
          .tutorial-section-content {
            padding: 0 1rem 1rem !important;
          }
          
          body .quick-start-box,
          .quick-start-box {
            padding: 1rem !important;
          }
          
          body .toc-button,
          .toc-button {
            width: 50px !important;
            height: 50px !important;
            font-size: 1.2rem !important;
            bottom: 1rem !important;
            right: 1rem !important;
          }
          
          /* Fix hero section */
          body .hero-section,
          .hero-section {
            padding: 1.5rem !important;
          }
          
          body .hero-section h1,
          .hero-section h1 {
            font-size: 1.75rem !important;
            line-height: 1.2 !important;
          }
          
          body .hero-section h2,
          .hero-section h2 {
            font-size: 1.2rem !important;
          }
          
          body .hero-section p,
          .hero-section p {
            font-size: 1rem !important;
          }
          
          /* Fix headings throughout */
          body h2,
          h2 {
            font-size: 1.4rem !important;
            line-height: 1.3 !important;
          }
          
          body h3,
          h3 {
            font-size: 1.2rem !important;
            line-height: 1.3 !important;
          }
          
          body h4,
          h4 {
            font-size: 1.05rem !important;
            line-height: 1.3 !important;
          }
          
          /* Fix various boxes */
          body .try-this-box,
          body .success-story-box,
          body .chunk-box,
          body .book-section-box,
          body .workflow-box,
          body .gnology-journal-box,
          body .pdf-download-box,
          body .journal-feature-box,
          body .tutorial-part,
          body .forensic-hero-box,
          body .forensic-comparison-box,
          body .forensic-tutorial-box,
          body .forensic-case-study-box,
          body .forensic-feature-box,
          .try-this-box,
          .success-story-box,
          .chunk-box,
          .book-section-box,
          .workflow-box,
          .gnology-journal-box,
          .pdf-download-box,
          .journal-feature-box,
          .tutorial-part,
          .forensic-hero-box,
          .forensic-comparison-box,
          .forensic-tutorial-box,
          .forensic-case-study-box,
          .forensic-feature-box {
            padding: 0.75rem 1rem !important;
            font-size: 0.95rem !important;
          }
          
          /* Fix grid layouts */
          body div[style*="grid-template-columns"],
          div[style*="grid-template-columns"] {
            grid-template-columns: 1fr !important;
          }
          
          /* Fix lists */
          body ul,
          body ol,
          ul,
          ol {
            padding-left: 1.5rem !important;
            font-size: 0.95rem !important;
          }
          
          /* Fix paragraphs */
          body p,
          p {
            font-size: 1rem !important;
            line-height: 1.6 !important;
          }
          
          /* Responsive link buttons */
          body a[style*="display: inline-block"],
          a[style*="display: inline-block"] {
            font-size: 1rem !important;
            padding: 0.6rem 1.5rem !important;
          }
          
          /* Remember section spacing fix - CRITICAL FOR FOOTER */
          body .remember-section,
          .remember-section {
            margin-top: 1.5rem !important;
            margin-bottom: 0.5rem !important;
          }
        }
        
        @media (max-width: 480px) {
          /* Even smaller screens */
          body .tutorial-section-button,
          .tutorial-section-button {
            font-size: 0.85rem !important;
            padding: 0.75rem !important;
          }
          
          body .tutorial-section-icon,
          .tutorial-section-icon {
            font-size: 1rem !important;
            margin-right: 0.4rem !important;
          }
          
          body .tutorial-container,
          .tutorial-container {
            padding: 0.75rem !important;
            padding-bottom: 0.25rem !important;
          }
          
          body .hero-section,
          .hero-section {
            padding: 1rem !important;
          }
          
          body .hero-section h1,
          .hero-section h1 {
            font-size: 1.5rem !important;
          }
          
          body .toc-button,
          .toc-button {
            width: 45px !important;
            height: 45px !important;
            font-size: 1rem !important;
          }
          
          body h2,
          h2 {
            font-size: 1.25rem !important;
          }
          
          body h3,
          h3 {
            font-size: 1.1rem !important;
          }
          
          body h4,
          h4 {
            font-size: 1rem !important;
          }
        }
        
        @media (max-width: 320px) {
          /* Ultra small screens */
          body .tutorial-section-button,
          .tutorial-section-button {
            font-size: 0.8rem !important;
            padding: 0.6rem !important;
          }
          
          body .tutorial-section-text,
          .tutorial-section-text {
            font-size: 0.9rem !important;
          }
          
          body .tutorial-container,
          .tutorial-container {
            padding: 0.5rem !important;
            padding-bottom: 0.25rem !important;
          }
        }
      `}} />

      {/* Hero Section */}
      <div className="hero-section" style={{
        background: 'white',
        borderRadius: '12px',
        padding: '3rem',
        marginBottom: '2rem',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        textAlign: 'center'
      }}>
        <h1 style={{fontSize: '2.5rem', fontWeight: 'bold', color: '#2e7d32', marginBottom: '1rem'}}>
          Complete Guide to Using Gene
        </h1>
        <h2 style={{fontSize: '1.5rem', color: '#555', fontWeight: 'normal'}}>
          Your Genealogy Research Assistant
        </h2>
        <p style={{fontSize: '1.1rem', color: '#666', marginTop: '1rem'}}>
          Master the art of family history research with Gene's expert guidance
        </p>
      </div>

      {/* Quick Start Guide */}
      <div className="quick-start-box">
        <h2 style={{fontSize: '1.8rem', color: '#2e7d32', marginBottom: '1rem'}}>
          🚀 Quick Start in 3 Easy Steps
        </h2>
        <ol style={{fontSize: '1.2rem', lineHeight: '2', paddingLeft: '1.5rem'}}>
          <li><strong>Tell Gene your research goal:</strong> "I want to find my Irish ancestors"</li>
          <li><strong>Upload a document or share what you know:</strong> Start with any family information</li>
          <li><strong>Follow Gene's research recommendations:</strong> Get step-by-step guidance</li>
        </ol>
        <button onClick={handleResearchPageClick} style={{
          display: 'inline-block',
          marginTop: '1rem',
          padding: '0.75rem 2rem',
          background: '#2e7d32',
          color: 'white',
          border: 'none',
          borderRadius: '50px',
          textDecoration: 'none',
          fontWeight: '600',
          fontSize: '1.1rem',
          cursor: 'pointer'
        }}>
          {isLoggedIn() ? 'Start Researching Now →' : 'Login to Start Researching →'}
        </button>
      </div>

      {/* Welcome Section */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '2rem',
        marginBottom: '2rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <p style={{fontSize: '1.1rem', lineHeight: '1.8', color: '#444'}}>
          Welcome to your comprehensive tutorial for working with Gene, your expert genealogical research 
          assistant powered by Generational Nexus (GNology™). This guide will help you maximize your family 
          history research experience and discover your ancestral story more effectively.
        </p>
      </div>

      {/* Tutorial Sections */}
      
      <TutorialSection 
        id="what-is-gene"
        title="What is Gene?" 
        icon="🧬"
        defaultOpen={true}
      >
        <p style={{fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '1rem'}}>
          Gene is an advanced genealogy assistant designed to guide you through every aspect of your family 
          history journey. Powered by Generational Nexus (GNology™) - a revolutionary genealogical knowledge 
          framework - Gene combines the expertise of professional genealogists, family historians, and research 
          specialists into one comprehensive research companion.
        </p>
        
        <h3 style={{color: '#2e7d32', marginTop: '1.5rem', marginBottom: '1rem'}}>
          Generational Nexus (GNology™) - Your Research Foundation
        </h3>
        <p style={{fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '1rem'}}>
          The GN in GNology stands for "Generational Nexus" - the central connection point linking all family 
          generations across time. This sophisticated framework enables Gene to:
        </p>
        <ul style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
          <li>Transform disconnected family facts into cohesive, meaningful narratives</li>
          <li>Create strategic connections between different generations</li>
          <li>Apply cutting-edge research methodologies with pattern recognition</li>
          <li>Provide comprehensive family storytelling capabilities</li>
        </ul>
      </TutorialSection>

      <TutorialSection 
        id="getting-started"
        title="Getting Started with Gene" 
        icon="🚀"
      >
        <h3 style={{color: '#2e7d32', marginBottom: '1rem'}}>Step 1: Introduce Your Research Goals</h3>
        <p style={{fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '1rem'}}>
          Begin by sharing your specific genealogy objectives with Gene:
        </p>
        <ul style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem', marginBottom: '1rem'}}>
          <li>"I'm trying to trace my Smith family line back to Ireland"</li>
          <li>"I need help organizing my grandmother's documents"</li>
          <li>"I want to break through a brick wall with my 3rd great-grandfather"</li>
          <li>"I'm new to genealogy and don't know where to start"</li>
        </ul>

        <div className="try-this-box">
          <strong>💡 Try This:</strong> "Gene, my grandfather was James Wilson born in 1920 in Chicago. 
          How do I start researching his family?"
        </div>

        <h3 style={{color: '#2e7d32', marginTop: '2rem', marginBottom: '1rem'}}>
          Step 2: Share What You Already Know
        </h3>
        <p style={{fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '1rem'}}>
          Gene works best when you provide context about your existing research:
        </p>
        <ul style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
          <li>Family names and relationships you've already identified</li>
          <li>Dates and locations you've confirmed</li>
          <li>Previous research you've completed</li>
          <li>Specific questions or mysteries you're trying to solve</li>
        </ul>

        <div className="success-story-box">
          <strong>Success Story:</strong> "Gene helped me discover my great-grandmother's maiden name by 
          suggesting I check her children's death certificates, where she was listed with her full name!"
        </div>
      </TutorialSection>

      <TutorialSection 
        id="building-family-tree"
        title="Building Your Family Tree with Gene" 
        icon="🌳"
      >
        <p style={{fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '1.5rem'}}>
          Gene can help you build your family tree through several powerful approaches, transforming scattered 
          information into a comprehensive family narrative.
        </p>

        <h3 style={{color: '#2e7d32', marginBottom: '1rem'}}>Systematic Tree Construction</h3>
        <div style={{background: '#e8f5e9', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
          <ul style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
            <li>Guide you through organized data collection starting from what you know</li>
            <li>Help structure your family information using professional genealogy standards</li>
            <li>Create comprehensive family group sheets that capture all vital details</li>
            <li>Ensure proper documentation and source citation for each family connection</li>
          </ul>
        </div>

        <h3 style={{color: '#2e7d32', marginBottom: '1rem'}}>Multi-Generational Analysis</h3>
        <div style={{background: '#e3f2fd', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
          <ul style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
            <li>Accurately count and organize generations (crucial for complex family trees)</li>
            <li>Identify missing links between generations</li>
            <li>Help resolve conflicting information about family relationships</li>
            <li>Connect scattered family branches into cohesive lineages</li>
          </ul>
        </div>

        <div className="try-this-box">
          <strong>💡 Try This:</strong> "Gene, I have some family information scattered across different documents. 
          Can you help me organize this into a proper family tree starting with my grandparents?"
        </div>
      </TutorialSection>

      <TutorialSection 
        id="gnology-research-journal"
        title="GNology Research Journal - Downloadable PDF Resource" 
        icon="📄"
      >
        <div className="gnology-journal-box">
          <h2 style={{fontSize: '1.8rem', color: '#2e7d32', marginBottom: '1rem', textAlign: 'center'}}>
            📄 GNOLOGY RESEARCH JOURNAL
          </h2>
          <p style={{fontSize: '1.2rem', textAlign: 'center', fontStyle: 'italic', color: '#555', marginBottom: '1.5rem'}}>
            Powered by Generational Nexus (GNology™) Methodology
          </p>
          <p style={{fontSize: '1.1rem', lineHeight: '1.8', textAlign: 'center'}}>
            The GNology Research Journal is a comprehensive PDF template that implements the proven 
            Generational Nexus methodology for systematic genealogical research.
          </p>
        </div>

        <h3 style={{color: '#2e7d32', marginTop: '2rem', marginBottom: '1rem'}}>
          What is the GNology Research Journal?
        </h3>
        <p style={{fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '1rem'}}>
          The GNology Research Journal is a downloadable PDF template that provides a structured framework 
          for organizing your genealogical research using the revolutionary <strong>5-Ancestor Focus</strong> 
          methodology. Instead of overwhelming yourself trying to research hundreds of family members 
          simultaneously, you focus on just <strong>5 key ancestors</strong> per project for maximum 
          research effectiveness.
        </p>

        <div style={{background: '#e8f5e9', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
          <h4 style={{color: '#1b5e20', marginBottom: '1rem'}}>Why the 5-Ancestor Focus?</h4>
          <ul style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
            <li><strong>Prevents Research Overwhelm:</strong> Focused approach vs. chaotic documentation</li>
            <li><strong>Maximizes Discoveries:</strong> Deep research on fewer people yields better results</li>
            <li><strong>Strategic Research:</strong> Systematic methodology proven by professional genealogists</li>
            <li><strong>Scalable Approach:</strong> Complete one journal, start another family line</li>
          </ul>
        </div>

        <div className="pdf-download-box">
          <h3 style={{fontSize: '1.5rem', marginBottom: '1rem'}}>📥 Download Your GNology Research Journal</h3>
          <p style={{fontSize: '1.1rem', marginBottom: '1.5rem', opacity: '0.9'}}>
            Get the complete PDF template with all forms, worksheets, and methodology guides
          </p>
          <a 
            href="/api/resources/gnology-journal.pdf" 
            download="GNology-Research-Journal.pdf"
            style={{
              display: 'inline-block',
              background: 'white',
              color: '#1565c0',
              padding: '1rem 2.5rem',
              borderRadius: '50px',
              textDecoration: 'none',
              fontWeight: '700',
              fontSize: '1.2rem',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
              transition: 'transform 0.3s',
              marginBottom: '1rem'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            📄 Download PDF Journal Template
          </a>
          <p style={{fontSize: '0.9rem', opacity: '0.8'}}>
            🖨️ Print-friendly format • 📱 Mobile-compatible • 🔄 Reusable template
          </p>
        </div>

        <h3 style={{color: '#2e7d32', marginTop: '2rem', marginBottom: '1rem'}}>
          Complete 10-Section Professional Research System
        </h3>
        
        <div style={{background: '#f8f9fa', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
          <p style={{fontSize: '1.05rem', lineHeight: '1.7', color: '#555', marginBottom: '1rem'}}>
            This is not just a simple template - it's a complete professional genealogical research system 
            used by professional genealogists and serious family historians. The journal includes advanced 
            features like DNA match tracking, geographic research tools, and sophisticated brick wall analysis.
          </p>
        </div>

        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', marginBottom: '2rem'}}>
          <div className="journal-feature-box">
            <h4 style={{color: '#2e7d32', marginBottom: '0.8rem'}}>📋 Section 1-2: Research Foundation</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li><strong>Research Planning & Goals:</strong> 5-ancestor priority system</li>
              <li><strong>Individual Ancestor Profiles:</strong> Complete life documentation</li>
              <li>Annual research goals by ancestor</li>
              <li>Monthly research scheduling templates</li>
              <li>Comprehensive vital information forms</li>
            </ul>
          </div>

          <div className="journal-feature-box">
            <h4 style={{color: '#2e7d32', marginBottom: '0.8rem'}}>👨‍👩‍👧‍👦 Section 3-4: Family Documentation</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li><strong>Family Group Records:</strong> Complete relationship mapping</li>
              <li><strong>Source Documentation:</strong> Professional citation standards</li>
              <li>Primary, secondary, and online source tracking</li>
              <li>Source quality rating system</li>
              <li>Repository and database management</li>
            </ul>
          </div>

          <div className="journal-feature-box">
            <h4 style={{color: '#2e7d32', marginBottom: '0.8rem'}}>📝 Section 5-6: Research Management</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li><strong>Research Logs:</strong> Daily activity tracking</li>
              <li><strong>DNA Research:</strong> Match analysis and strategy</li>
              <li>Search strategy documentation</li>
              <li>Negative results tracking</li>
              <li>Correspondence management</li>
            </ul>
          </div>

          <div className="journal-feature-box">
            <h4 style={{color: '#2e7d32', marginBottom: '0.8rem'}}>🧩 Section 7-8: Advanced Analysis</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li><strong>Brick Wall Analysis:</strong> Systematic problem-solving</li>
              <li><strong>Timeline Comparisons:</strong> Multi-ancestor analysis</li>
              <li>Alternative theory development</li>
              <li>Cluster research planning (FAN method)</li>
              <li>Historical context integration</li>
            </ul>
          </div>

          <div className="journal-feature-box">
            <h4 style={{color: '#2e7d32', marginBottom: '0.8rem'}}>🗺️ Section 9-10: Specialized Research</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li><strong>Geographic Research:</strong> Location-based analysis</li>
              <li><strong>Document Inventory:</strong> Complete archival system</li>
              <li>Historical map consultation tracking</li>
              <li>Migration pattern analysis</li>
              <li>Professional document management</li>
            </ul>
          </div>

          <div className="journal-feature-box">
            <h4 style={{color: '#2e7d32', marginBottom: '0.8rem'}}>🧬 Advanced DNA Integration</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li>DNA match tracking by ancestor connection</li>
              <li>Shared match analysis workflows</li>
              <li>DNA research priority management</li>
              <li>Chromosome mapping integration</li>
              <li>Match correspondence tracking</li>
            </ul>
          </div>
        </div>

        <h3 style={{color: '#2e7d32', marginTop: '2rem', marginBottom: '1rem'}}>
          How to Use Your Downloaded Journal
        </h3>

        <div className="workflow-box">
          <h4 style={{color: '#1b5e20', marginBottom: '1rem'}}>Step 1: Print and Setup</h4>
          <ol style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
            <li><strong>Download the PDF:</strong> Save the complete journal template to your device</li>
            <li><strong>Print Pages:</strong> Print the sections you need or the entire journal</li>
            <li><strong>Choose 5 Ancestors:</strong> Select your most important research targets</li>
            <li><strong>Set Priorities:</strong> Rank ancestors 1-5 based on research importance</li>
          </ol>
        </div>

        <div className="workflow-box">
          <h4 style={{color: '#1b5e20', marginBottom: '1rem'}}>Step 2: Research Planning</h4>
          <ol style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
            <li><strong>Set Research Goals:</strong> Use the templates to define specific objectives</li>
            <li><strong>Document Brick Walls:</strong> Identify current research challenges</li>
            <li><strong>Develop Strategies:</strong> Create action plans using the worksheets</li>
            <li><strong>Track Progress:</strong> Monitor goal completion with the tracking forms</li>
          </ol>
        </div>

        <div className="workflow-box">
          <h4 style={{color: '#1b5e20', marginBottom: '1rem'}}>Step 3: Research Execution</h4>
          <ol style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
            <li><strong>Fill Out Forms:</strong> Complete family group sheets and research logs</li>
            <li><strong>Document Sources:</strong> Use citation templates for all discoveries</li>
            <li><strong>Create Timelines:</strong> Build chronological family events</li>
            <li><strong>Analyze Evidence:</strong> Evaluate findings using the analysis forms</li>
          </ol>
        </div>

        <div style={{background: '#e8f5e9', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
          <h4 style={{color: '#1b5e20', marginBottom: '1rem'}}>💡 Pro Tips for Journal Success:</h4>
          <ul style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
            <li><strong>Start Small:</strong> Begin with one family line and expand from there</li>
            <li><strong>Stay Focused:</strong> Resist the urge to research beyond your 5 chosen ancestors</li>
            <li><strong>Document Everything:</strong> Record negative searches and conflicting information</li>
            <li><strong>Review Regularly:</strong> Weekly progress reviews keep research on track</li>
            <li><strong>Create Multiple Journals:</strong> Use separate journals for different family lines</li>
          </ul>
        </div>

        <div className="success-story-box">
          <strong>Success Story:</strong> "Using the printed GNology Research Journal, I organized 20 years 
          of scattered research into focused projects. The 5-ancestor limit helped me finally break through 
          three brick walls I'd been stuck on for years by allowing deep, concentrated research instead of 
          surface-level documentation!"
        </div>

        <div style={{background: '#fff3e0', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
          <p style={{fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '1rem'}}>
            <strong>Need Research Strategy Guidance?</strong> After downloading your journal and identifying 
            your research challenges, visit the Gene Research page for expert guidance on developing effective 
            strategies and overcoming brick walls.
          </p>
          <p style={{fontSize: '1.05rem', lineHeight: '1.7'}}>
            <strong>Workflow:</strong> Download journal → Set up your 5-ancestor research plan → Visit Gene 
            for detailed strategy development and expert guidance on next steps.
          </p>
        </div>

        <div style={{textAlign: 'center', marginTop: '2rem'}}>
          <button onClick={handleResourcesPageClick} style={{
            display: 'inline-block',
            padding: '1rem 2.5rem',
            background: '#2e7d32',
            color: 'white',
            border: 'none',
            borderRadius: '50px',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '1.2rem',
            marginRight: '1rem',
            marginBottom: '1rem',
            cursor: 'pointer',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
          }}>
            📄 Go to Resources Page for PDF Download →
          </button>
          <button onClick={handleResearchPageClick} style={{
            display: 'inline-block',
            padding: '1rem 2.5rem',
            background: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '50px',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '1.2rem',
            marginBottom: '1rem',
            cursor: 'pointer',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
          }}>
            {isLoggedIn() ? '🔍 Get Research Strategy Help →' : 'Login for Research Guidance →'}
          </button>
        </div>
      </TutorialSection>

      {/* NEW FORENSIC GENEALOGY SECTION */}
      <TutorialSection 
        id="forensic-genealogy"
        title="Forensic Genealogy with Gene AI" 
        icon="🏛️"
      >
        <div className="forensic-hero-box">
          <h2 style={{fontSize: '1.8rem', marginBottom: '1rem', textAlign: 'center'}}>
            🏛️ FORENSIC GENEALOGY WITH GENE AI
          </h2>
          <p style={{fontSize: '1.2rem', textAlign: 'center', fontStyle: 'italic', marginBottom: '1.5rem'}}>
            Systematic Precision for Legal-Grade Genealogical Research
          </p>
          <p style={{fontSize: '1.1rem', lineHeight: '1.8', textAlign: 'center'}}>
            Gene AI brings systematic precision to forensic genealogy's "can't afford to be wrong" standard 
            through advanced verification protocols and legal-grade documentation.
          </p>
        </div>

        <div style={{background: '#f8f9fa', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
          <h4 style={{color: '#d32f2f', marginBottom: '1rem'}}>What is Forensic Genealogy?</h4>
          <p style={{fontSize: '1.05rem', lineHeight: '1.7'}}>
            Forensic genealogy applies genealogical research methods to legal problems, including missing heirs, 
            criminal investigations, military repatriation, and identity verification. Unlike traditional genealogy, 
            forensic work requires legal-grade accuracy and court-admissible documentation.
          </p>
        </div>

        <h3 style={{color: '#2e7d32', marginTop: '2rem', marginBottom: '1rem'}}>
          Key Differences from Traditional Genealogy
        </h3>

        <div className="forensic-comparison-box">
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', marginBottom: '1.5rem'}}>
            <div style={{background: '#e8f5e9', padding: '1rem', borderRadius: '8px'}}>
              <h4 style={{color: '#1b5e20', marginBottom: '0.8rem'}}>Traditional Genealogy</h4>
              <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
                <li>Focus: Looking backward for ancestors</li>
                <li>Standard: "Preponderance of evidence"</li>
                <li>Timeline: Flexible research pace</li>
                <li>Documentation: Family history standards</li>
                <li>Skills: Research and analysis</li>
                <li>Audience: Family members</li>
              </ul>
            </div>
            <div style={{background: '#ffebee', padding: '1rem', borderRadius: '8px'}}>
              <h4 style={{color: '#d32f2f', marginBottom: '0.8rem'}}>Forensic Genealogy</h4>
              <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
                <li>Focus: Working forward to living descendants</li>
                <li>Standard: "Can't afford to be wrong"</li>
                <li>Timeline: Strict legal deadlines</li>
                <li>Documentation: Legal admissibility required</li>
                <li>Skills: Research + investigation + legal testimony</li>
                <li>Audience: Courts, law enforcement, attorneys</li>
              </ul>
            </div>
          </div>
        </div>

        <h3 style={{color: '#2e7d32', marginTop: '2rem', marginBottom: '1rem'}}>
          Common Forensic Genealogy Applications
        </h3>

        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', marginBottom: '2rem'}}>
          <div className="forensic-feature-box">
            <h4 style={{color: '#2e7d32', marginBottom: '0.8rem'}}>⚖️ Property Rights & Inheritance</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li>Missing heir identification for estates</li>
              <li>Probate research and will contests</li>
              <li>Mineral rights and royalty distributions</li>
              <li>Unclaimed property claims</li>
              <li>Real estate ownership disputes</li>
            </ul>
          </div>

          <div className="forensic-feature-box">
            <h4 style={{color: '#2e7d32', marginBottom: '0.8rem'}}>🏛️ Legal & Government Applications</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li>Military repatriation (identifying fallen service members)</li>
              <li>Immigration and citizenship verification</li>
              <li>Tribal membership qualification</li>
              <li>Social Security benefit claims</li>
              <li>Adoptee rights and birth parent location</li>
            </ul>
          </div>

          <div className="forensic-feature-box">
            <h4 style={{color: '#2e7d32', marginBottom: '0.8rem'}}>🔍 Criminal Justice Applications</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li>Cold case investigations using DNA</li>
              <li>Victim identification from remains</li>
              <li>Suspect identification through genetic genealogy</li>
              <li>Mass disaster victim identification</li>
            </ul>
          </div>
        </div>

        <h3 style={{color: '#2e7d32', marginTop: '2rem', marginBottom: '1rem'}}>
          Gene's Forensic Capabilities
        </h3>

        <div className="forensic-tutorial-box">
          <h4 style={{color: '#d32f2f', marginBottom: '1rem'}}>The GNology Forensic Protocol:</h4>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem'}}>
            <div style={{background: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid #e0e0e0'}}>
              <h5 style={{color: '#1565c0', marginBottom: '0.5rem'}}>PHASE 1: EVIDENCE COLLECTION</h5>
              <ul style={{paddingLeft: '1rem', fontSize: '0.9rem', lineHeight: '1.5'}}>
                <li>Document Analysis with OCR Precision</li>
                <li>DNA Data Integration and Verification</li>
                <li>Multi-Source Cross-Referencing</li>
                <li>Historical Context Analysis</li>
              </ul>
            </div>
            <div style={{background: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid #e0e0e0'}}>
              <h5 style={{color: '#1565c0', marginBottom: '0.5rem'}}>PHASE 2: RELATIONSHIP VERIFICATION</h5>
              <ul style={{paddingLeft: '1rem', fontSize: '0.9rem', lineHeight: '1.5'}}>
                <li>Automated Conflict Detection</li>
                <li>Timeline Consistency Checking</li>
                <li>Geographic Movement Patterns</li>
                <li>Family Structure Analysis</li>
              </ul>
            </div>
            <div style={{background: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid #e0e0e0'}}>
              <h5 style={{color: '#1565c0', marginBottom: '0.5rem'}}>PHASE 3: LEGAL DOCUMENTATION</h5>
              <ul style={{paddingLeft: '1rem', fontSize: '0.9rem', lineHeight: '1.5'}}>
                <li>Court-Ready Evidence Compilation</li>
                <li>Chain of Custody Documentation</li>
                <li>Expert Report Generation</li>
                <li>Professional Citation Standards</li>
              </ul>
            </div>
          </div>
        </div>

        <h3 style={{color: '#2e7d32', marginTop: '2rem', marginBottom: '1rem'}}>
          Gene's Forensic Advantages
        </h3>

        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', marginBottom: '2rem'}}>
          <div style={{background: '#e8f5e9', padding: '1.5rem', borderRadius: '8px'}}>
            <h4 style={{color: '#1b5e20', marginBottom: '1rem'}}>⚡ Error Reduction Through AI</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '1.05rem', lineHeight: '1.7'}}>
              <li>90%+ reduction in research errors through systematic verification</li>
              <li>Automated conflict detection across multiple sources</li>
              <li>Cross-referencing capabilities impossible with manual research</li>
              <li>Built-in quality control checkpoints</li>
            </ul>
          </div>

          <div style={{background: '#e3f2fd', padding: '1.5rem', borderRadius: '8px'}}>
            <h4 style={{color: '#1565c0', marginBottom: '1rem'}}>🚀 Time Efficiency</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '1.05rem', lineHeight: '1.7'}}>
              <li>50-70% faster case completion while maintaining accuracy</li>
              <li>Automated document analysis and data extraction</li>
              <li>Systematic heir identification processes</li>
              <li>Strategic research pathway optimization</li>
            </ul>
          </div>

          <div style={{background: '#fff3e0', padding: '1.5rem', borderRadius: '8px'}}>
            <h4 style={{color: '#e65100', marginBottom: '1rem'}}>⚖️ Legal Compliance</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '1.05rem', lineHeight: '1.7'}}>
              <li>Professional genealogical standards (GPS) integration</li>
              <li>Court-admissible documentation formatting</li>
              <li>Chain of custody tracking for evidence</li>
              <li>Expert witness report generation</li>
            </ul>
          </div>
        </div>

        <h3 style={{color: '#2e7d32', marginTop: '2rem', marginBottom: '1rem'}}>
          Interactive Forensic Tutorials
        </h3>

        <div className="forensic-case-study-box">
          <h4 style={{color: '#3f51b5', marginBottom: '1rem'}}>Tutorial 1: Missing Heir Case Walkthrough</h4>
          <p style={{fontSize: '1.05rem', fontStyle: 'italic', marginBottom: '1rem'}}>
            Scenario: You've been hired to identify heirs for a $340,000 estate. The deceased never married, 
            had no children, and no known living relatives. You have 8 weeks to provide court-ready proof 
            of rightful heirs.
          </p>
          <div style={{background: 'white', padding: '1rem', borderRadius: '8px', marginTop: '1rem'}}>
            <h5 style={{color: '#2e7d32', marginBottom: '0.5rem'}}>Step-by-Step Process:</h5>
            <ol style={{paddingLeft: '1.5rem', fontSize: '1rem', lineHeight: '1.6'}}>
              <li><strong>Initial Document Analysis:</strong> Upload death certificate, extract key genealogical information</li>
              <li><strong>Family Tree Construction:</strong> Systematic sibling identification and marriage record location</li>
              <li><strong>DNA Integration:</strong> Genetic genealogy for verification and relationship confirmation</li>
              <li><strong>Legal Documentation:</strong> Evidence compilation for court and expert report formatting</li>
            </ol>
          </div>
        </div>

        <div className="forensic-case-study-box">
          <h4 style={{color: '#3f51b5', marginBottom: '1rem'}}>Tutorial 2: Cold Case DNA Analysis</h4>
          <p style={{fontSize: '1.05rem', fontStyle: 'italic', marginBottom: '1rem'}}>
            Scenario: Law enforcement has DNA from a 1985 cold case with matches in genealogy databases. 
            Build investigative family trees to identify potential suspects.
          </p>
          <div style={{background: 'white', padding: '1rem', borderRadius: '8px', marginTop: '1rem'}}>
            <h5 style={{color: '#2e7d32', marginBottom: '0.5rem'}}>Learning Modules:</h5>
            <ul style={{paddingLeft: '1.5rem', fontSize: '1rem', lineHeight: '1.6'}}>
              <li><strong>DNA Match Analysis:</strong> Understanding genetic distance and building trees around matches</li>
              <li><strong>Timeline Construction:</strong> Creating chronological frameworks and eliminating suspects</li>
              <li><strong>Evidence Standards:</strong> Legal requirements for DNA evidence and privacy considerations</li>
            </ul>
          </div>
        </div>

        <div className="forensic-case-study-box">
          <h4 style={{color: '#3f51b5', marginBottom: '1rem'}}>Tutorial 3: Military Repatriation Research</h4>
          <p style={{fontSize: '1.05rem', fontStyle: 'italic', marginBottom: '1rem'}}>
            Scenario: The Department of Defense needs to identify family members for a WWII serviceman's 
            remains repatriation ceremony.
          </p>
          <div style={{background: 'white', padding: '1rem', borderRadius: '8px', marginTop: '1rem'}}>
            <h5 style={{color: '#2e7d32', marginBottom: '0.5rem'}}>Learning Components:</h5>
            <ul style={{paddingLeft: '1.5rem', fontSize: '1rem', lineHeight: '1.6'}}>
              <li>Military record analysis techniques</li>
              <li>Government database navigation</li>
              <li>Family notification protocols</li>
              <li>International research considerations</li>
            </ul>
          </div>
        </div>

        <h3 style={{color: '#2e7d32', marginTop: '2rem', marginBottom: '1rem'}}>
          Legal and Ethical Considerations
        </h3>

        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', marginBottom: '2rem'}}>
          <div style={{background: '#fff8e1', padding: '1.5rem', borderRadius: '8px', border: '1px solid #ffc107'}}>
            <h4 style={{color: '#f57c00', marginBottom: '1rem'}}>🔒 Privacy and Consent Issues</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li>Consent requirements for genetic genealogy</li>
              <li>Privacy laws affecting genealogical research</li>
              <li>Living person information restrictions</li>
              <li>Database terms of service compliance</li>
            </ul>
          </div>

          <div style={{background: '#e8eaf6', padding: '1.5rem', borderRadius: '8px', border: '1px solid #3f51b5'}}>
            <h4 style={{color: '#3f51b5', marginBottom: '1rem'}}>👮‍♂️ Working with Law Enforcement</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li>Understanding law enforcement needs</li>
              <li>Evidence documentation requirements</li>
              <li>Court testimony preparation</li>
              <li>Professional licensing considerations</li>
            </ul>
          </div>
        </div>

        <h3 style={{color: '#2e7d32', marginTop: '2rem', marginBottom: '1rem'}}>
          Professional Development in Forensic Genealogy
        </h3>

        <div className="forensic-feature-box">
          <h4 style={{color: '#2e7d32', marginBottom: '1rem'}}>Career Path Requirements:</h4>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem'}}>
            <div>
              <h5 style={{color: '#1b5e20', marginBottom: '0.5rem'}}>Required Skills:</h5>
              <ul style={{paddingLeft: '1rem', fontSize: '0.9rem', lineHeight: '1.5'}}>
                <li>Traditional genealogical research expertise</li>
                <li>DNA and genetic genealogy understanding</li>
                <li>Legal knowledge and court procedures</li>
                <li>Investigation and interviewing skills</li>
              </ul>
            </div>
            <div>
              <h5 style={{color: '#1b5e20', marginBottom: '0.5rem'}}>Certification & Licensing:</h5>
              <ul style={{paddingLeft: '1rem', fontSize: '0.9rem', lineHeight: '1.5'}}>
                <li>Professional genealogy certifications</li>
                <li>Private investigator licensing requirements</li>
                <li>Continuing education opportunities</li>
                <li>Professional association memberships</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="try-this-box">
          <strong>💡 Try Forensic Research with Gene:</strong> "Gene, I need to identify potential heirs for 
          an estate case. The deceased was John Smith (1935-2023) who never married and had no children. 
          Can you help me develop a systematic approach to identify his closest living relatives for legal purposes?"
        </div>

        <h3 style={{color: '#2e7d32', marginTop: '2rem', marginBottom: '1rem'}}>
          Getting Started with Gene for Forensic Work
        </h3>

        <div className="workflow-box">
          <h4 style={{color: '#1b5e20', marginBottom: '1rem'}}>Setting Up for Forensic Cases:</h4>
          <ol style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
            <li><strong>Download the GNology Research Journal</strong> for systematic documentation</li>
            <li><strong>Configure verification protocols</strong> for legal-grade research standards</li>
            <li><strong>Establish documentation workflows</strong> for court-ready evidence compilation</li>
            <li><strong>Set up export formats</strong> for professional reports and expert testimony</li>
          </ol>
        </div>

        <div className="success-story-box">
          <strong>Forensic Success Story:</strong> "Using Gene's forensic verification protocols, I identified 
          the rightful heir to a $2.3 million estate in just 6 weeks. The systematic approach and automated 
          conflict detection helped me build an airtight case that was accepted by the court without challenge. 
          Gene's documentation standards saved me days of preparation for expert testimony."
        </div>

        <div style={{textAlign: 'center', marginTop: '2rem'}}>
          <button onClick={handleResearchPageClick} style={{
            display: 'inline-block',
            padding: '1rem 2.5rem',
            background: '#d32f2f',
            color: 'white',
            border: 'none',
            borderRadius: '50px',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '1.2rem',
            marginBottom: '1rem',
            cursor: 'pointer',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
          }}>
            {isLoggedIn() ? '🏛️ Start Forensic Research Project →' : 'Login for Forensic Research →'}
          </button>
        </div>
      </TutorialSection>

      <TutorialSection 
        id="document-analysis"
        title="Working with Documents - Gene's Powerful Analysis" 
        icon="📄"
      >
        <h3 style={{color: '#2e7d32', marginBottom: '1rem'}}>Document Upload and Processing</h3>
        <p style={{fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '1rem'}}>
          Gene can analyze various document types to extract genealogical information:
        </p>

        <div style={{background: '#f5f5f5', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
          <h4 style={{color: '#1b5e20', marginBottom: '0.5rem'}}>Supported Document Formats:</h4>
          <ul style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
            <li>PDF files (birth certificates, marriage records, death certificates)</li>
            <li>Image files (JPG, PNG) of documents or photographs</li>
            <li>Word documents (DOC, DOCX)</li>
            <li>Text files (TXT)</li>
          </ul>
          <p style={{fontSize: '0.95rem', color: '#666', marginTop: '0.5rem'}}>
            <strong>Maximum file size:</strong> 200MB per document
          </p>
        </div>

        <h4 style={{color: '#1b5e20', marginBottom: '1rem'}}>How to Upload Documents:</h4>
        <ol style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem', marginBottom: '1.5rem'}}>
          <li>Click the attachment or upload button in your interface</li>
          <li>Select your genealogical document</li>
          <li>Gene will process the document using advanced OCR (Optical Character Recognition)</li>
          <li>Receive detailed analysis with extracted genealogical data</li>
        </ol>

        <div className="try-this-box">
          <strong>💡 Try This:</strong> "Gene, I've uploaded my grandmother's 1940 census record. 
          Can you analyze it and tell me what clues it provides for further research?"
        </div>

        <h3 style={{color: '#2e7d32', marginTop: '2rem', marginBottom: '1rem'}}>
          What Gene Extracts from Documents
        </h3>

        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', marginBottom: '1.5rem'}}>
          <div style={{background: '#e8f5e9', padding: '1.5rem', borderRadius: '8px'}}>
            <h4 style={{color: '#1b5e20', marginBottom: '0.8rem'}}>Census Records Analysis:</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li>Complete household composition and relationships</li>
              <li>Ages, birthplaces, and occupations</li>
              <li>Property values and economic indicators</li>
              <li>Immigration and naturalization status</li>
              <li>Literacy and education information</li>
              <li>Analysis of neighboring households</li>
            </ul>
          </div>

          <div style={{background: '#e3f2fd', padding: '1.5rem', borderRadius: '8px'}}>
            <h4 style={{color: '#1565c0', marginBottom: '0.8rem'}}>Vital Records Processing:</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li>Full names including maiden names</li>
              <li>Exact dates and locations of events</li>
              <li>Parents' names and birthplaces</li>
              <li>Informant information and relationships</li>
              <li>Witnesses and officiants</li>
              <li>Medical information and causes of death</li>
            </ul>
          </div>

          <div style={{background: '#fff3e0', padding: '1.5rem', borderRadius: '8px'}}>
            <h4 style={{color: '#e65100', marginBottom: '0.8rem'}}>Military Records Examination:</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li>Service dates and unit information</li>
              <li>Rank progression and assignments</li>
              <li>Physical descriptions</li>
              <li>Next of kin and beneficiary details</li>
              <li>Pension application information</li>
            </ul>
          </div>

          <div style={{background: '#fce4ec', padding: '1.5rem', borderRadius: '8px'}}>
            <h4 style={{color: '#c2185b', marginBottom: '0.8rem'}}>Photo and Image Analysis:</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li>Dating estimates from techniques/fashion</li>
              <li>Photo type identification</li>
              <li>Historical context from settings/props</li>
              <li>Family relationship possibilities</li>
            </ul>
          </div>
        </div>

        <h3 style={{color: '#2e7d32', marginTop: '2rem', marginBottom: '1rem'}}>
          Document Download and Export Options
        </h3>
        <p style={{fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '1rem'}}>
          After Gene analyzes your documents, you can:
        </p>
        <ul style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
          <li>Download analysis results in multiple formats (TXT, JSON, MD)</li>
          <li>Export extracted text content and metadata</li>
          <li>Save Gene's genealogical analysis for your records</li>
          <li>Create organized files for your family history documentation</li>
        </ul>

        <div className="try-this-box">
          <strong>💡 Pro Tip:</strong> Use Gene's document analysis to fill out the forms in your 
          downloaded GNology Research Journal for systematic organization of your findings!
        </div>
      </TutorialSection>

      <TutorialSection 
        id="document-photo-integration"
        title="Precision Document and Photo Integration for Family History" 
        icon="📸"
      >
        <p style={{fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '1.5rem'}}>
          Gene excels at helping you strategically place photos and documents to create compelling, professional 
          family history presentations and books. This approach combines genealogical expertise with thoughtful 
          visual storytelling.
        </p>

        <h3 style={{color: '#2e7d32', marginBottom: '1rem'}}>Strategic Document Placement Framework</h3>
        
        <div style={{background: '#e8f5e9', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
          <h4 style={{color: '#1b5e20', marginBottom: '1rem'}}>Chronological Integration</h4>
          <ul style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
            <li>Position documents at precise historical moments in your family narrative</li>
            <li>Sequence photos to show family progression across generations</li>
            <li>Align supporting documents (census records, vital records) with specific life events</li>
            <li>Create visual timelines where documents reinforce the story flow</li>
          </ul>
        </div>

        <div className="try-this-box">
          <strong>💡 Try This:</strong> "Gene, I have 50 family photos and 20 documents. Can you help me 
          create a placement strategy for a family history book organized by generations?"
        </div>
      </TutorialSection>

      <TutorialSection 
        id="family-history-book-writing"
        title="Complete Family History Book Writing Tutorial" 
        icon="📚"
      >
        <div className="book-section-box">
          <h2 style={{fontSize: '1.8rem', color: '#7b1fa2', marginBottom: '1rem', textAlign: 'center'}}>
            📚 How Gene AI Writes Your Complete Family History Book
          </h2>
          <p style={{fontSize: '1.2rem', textAlign: 'center', fontStyle: 'italic', color: '#555', marginBottom: '1.5rem'}}>
            Powered by Generational Nexus (GNology)
          </p>
          <p style={{fontSize: '1.1rem', lineHeight: '1.8', textAlign: 'center'}}>
            Welcome to Gene's comprehensive guide for writing your family history book! Whether you're documenting 
            a single family line or creating a multi-generational saga, I'm here to help you craft a compelling 
            narrative that honors your ancestors and preserves their stories for future generations.
          </p>
        </div>
        
        <div className="tutorial-part">
          <h4 style={{color: '#7b1fa2', marginBottom: '1rem'}}>Part 1: Setup Process</h4>
          <ol style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
            <li>Sign up at www.mygnology.com</li>
            <li>Download the Fillable GNology Journal from the "Gene" Resource section/tab</li>
            <li>Save to desktop/thumb drive for easy access</li>
            <li>Fill out the journal completely - save frequently with family name in filename</li>
            <li>Upload to Documents section - journal plus any family-specific notes</li>
            <li>Navigate to Research section</li>
            <li>Select Documents - Choose ONLY the GNology Journal and related notes (save census, certificates, photos for later)</li>
            <li>Create New Conversation - Click green "New" button</li>
            <li>Rename the session - Use pencil icon to edit name to "Smith Family Book Part 1" (replace "Smith" with your family name)</li>
            <li><strong>Type the magic words:</strong> "Write the book"</li>
          </ol>
        </div>

        <div className="tutorial-part" style={{marginTop: '2rem'}}>
          <h4 style={{color: '#7b1fa2', marginBottom: '1rem'}}>Part 2: Gene's Automated Process & Viewing Options</h4>
          <p style={{fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '1rem'}}>
            Once you type "Write the book," Gene automatically responds with:
          </p>
          <div style={{background: '#e8f5e9', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
            <p style={{fontStyle: 'italic', marginBottom: '1rem'}}>
              "I've received your GNology Journal for the [Family Name] family! I can see information about [summarize key ancestors]. Let me create your comprehensive family history book."
            </p>
            <p><strong>Book Title Suggestions:</strong></p>
            <ul style={{marginLeft: '1rem', marginBottom: '1rem'}}>
              <li>[Generate 3 title options based on journal content]</li>
              <li>Or you can choose your own title</li>
            </ul>
            <p><strong>My Book Creation Process:</strong></p>
            <ol style={{marginLeft: '1rem'}}>
              <li>Table of Contents & Introduction (First)</li>
              <li>Chapters 1-3 (Early generations/origins)</li>
              <li>Chapters 4-6 (Middle generations/migrations)</li>
              <li>Chapters 7-9 (Recent generations/modern era)</li>
              <li>Conclusion & Appendices</li>
              <li>Photo/Document Integration (Upload those saved documents now!)</li>
            </ol>
          </div>
          <div style={{background: '#fff3e0', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
            <p style={{color: '#e65100', fontWeight: 'bold', marginBottom: '1rem'}}>
              CRITICAL STEP - Save Gene's Format:
            </p>
            <p style={{fontSize: '1.05rem', lineHeight: '1.7'}}>
              Before responding "Yes" - Copy and paste Gene's entire response to a Word Doc or Google Doc and name it "Smith Family Format" (or whatever name of the family). This saved Format document will be needed for additional Conversations.
            </p>
          </div>
          <p style={{fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '1rem'}}>
            <strong>Enhanced Viewing Experience:</strong> Click the green "Fullscreen" button on the right-hand side for a better view of your manuscript as Gene writes it.
          </p>
        </div>

        <div className="tutorial-part" style={{marginTop: '2rem'}}>
          <h4 style={{color: '#7b1fa2', marginBottom: '1rem'}}>Part 3: The Writing Process</h4>
          <p style={{fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '1rem'}}>
            Gene creates:
          </p>
          <ul style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem', marginBottom: '1.5rem'}}>
            <li>Professional Table of Contents based on family structure</li>
            <li>Engaging Introduction with family overview and research journey</li>
            <li>Chronological chapters organized by generations or themes</li>
            <li>Historical context woven throughout each chapter</li>
            <li>Transition paragraphs connecting different family lines</li>
            <li>Proper citations for all sources referenced</li>
          </ul>
          <p style={{fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '1rem'}}>
            After each section, Gene offers:
          </p>
          <ul style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
            <li>"Review this section? (Download TXT/PDF)"</li>
            <li>"Continue to next chapters?"</li>
            <li>"Add more details to current section?"</li>
            <li>"Ready for photos/documents integration?" (Save this for the very end)</li>
          </ul>
          <div style={{background: '#e3f2fd', padding: '1.5rem', borderRadius: '8px', marginTop: '1.5rem'}}>
            <p style={{color: '#1565c0', fontWeight: 'bold', marginBottom: '0.5rem'}}>
              Simple Continuation:
            </p>
            <p style={{fontSize: '1.05rem', lineHeight: '1.7'}}>
              In the Dialogue box at the bottom, you can just type: <strong>"continue"</strong>
              <br />Gene will pick up exactly from where it left off (mid-sentence or mid-word).
            </p>
          </div>
        </div>

        <div className="tutorial-part" style={{marginTop: '2rem'}}>
          <h4 style={{color: '#7b1fa2', marginBottom: '1rem'}}>Part 4: Managing Conversation Limits</h4>
          <div style={{background: '#fff8e1', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
            <p style={{color: '#f57c00', fontWeight: 'bold', marginBottom: '1rem'}}>
              Important Note: If Gene displays: "This conversation has reached Gene's maximum capacity. Please start a new conversation to continue."
            </p>
            <p style={{fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '1rem'}}>What to do:</p>
            <ol style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
              <li>Start a new conversation - Click the green "New" button</li>
              <li>Name the new session - Use the pencil icon to rename it "Smith Family Book Part 2"</li>
              <li>Upload your saved Format - Go to Documents section and upload the "Smith Family Format" document you saved earlier</li>
              <li>Upload the GNology Journal - Provide context for the new conversation</li>
              <li>Select both documents - Go back to Research section and select both documents</li>
              <li>Continue seamlessly - Type "Continue writing the Smith family book starting with Chapter X" (specify the incomplete chapter)</li>
            </ol>
          </div>
        </div>

        <div className="tutorial-part" style={{marginTop: '2rem'}}>
          <h4 style={{color: '#7b1fa2', marginBottom: '1rem'}}>Part 5: Final Integration & Publishing</h4>
          <p style={{fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '1rem'}}>
            When ready for photos/documents: "Now upload those census records, certificates, and photos you saved! I'll:
          </p>
          <ul style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem', marginBottom: '1.5rem'}}>
            <li>Suggest optimal placement throughout the book</li>
            <li>Create captions connecting them to the narrative</li>
            <li>Integrate them seamlessly into existing chapters</li>
            <li>Ensure they enhance rather than interrupt the story flow</li>
          </ul>
          <div style={{background: '#e8f5e9', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
            <p style={{color: '#2e7d32', fontWeight: 'bold', marginBottom: '1rem'}}>
              📚 Publishing Resources:
            </p>
            <ul style={{paddingLeft: '1rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
              <li><strong>Professional Book Cover Design:</strong> Get custom covers on Fiverr for $20-$30</li>
              <li><strong>Free Book Cover Creation:</strong> Use Lulu's Cover Creator with step-by-step tutorials</li>
              <li><strong>Self-Publishing Guide:</strong> Complete tutorial on publishing your family history book</li>
            </ul>
          </div>
          <p style={{fontSize: '1.05rem', lineHeight: '1.7', color: '#2e7d32', fontWeight: 'bold'}}>
            Final Product: Complete family history book (typically 40,000-80,000 words) with professional formatting, integrated photos, comprehensive citations, and ready for printing or digital sharing.
          </p>
        </div>

        <div className="try-this-box">
          <strong>💡 Ready to Begin Your Family History Book?</strong> 
          <button onClick={handleResearchPageClick} style={{
            display: 'block',
            marginTop: '1rem',
            padding: '0.75rem 2rem',
            background: '#7b1fa2',
            color: 'white',
            border: 'none',
            borderRadius: '25px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
            width: '100%'
          }}>
            {isLoggedIn() ? 'Start Your Book Project Now →' : 'Login to Start Your Book Project →'}
          </button>
          <p style={{fontSize: '0.9rem', marginTop: '0.5rem', textAlign: 'center', color: '#666'}}>
            Download your GNology Journal, fill it out completely, then return to start writing your family's story!
          </p>
        </div>
      </TutorialSection>

      <TutorialSection 
        id="research-guidance"
        title="Research Guidance and Strategy Development" 
        icon="🔍"
      >
        <h3 style={{color: '#2e7d32', marginBottom: '1rem'}}>Creating Research Plans</h3>
        <p style={{fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '1.5rem'}}>
          Gene helps you develop systematic approaches to your family history:
        </p>

        <div style={{background: '#f5f5f5', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
          <h4 style={{color: '#1b5e20', marginBottom: '1rem'}}>Assessment Phase:</h4>
          <ul style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem'}}>
            <li>Evaluation of your current knowledge and documentation</li>
            <li>Identification of research gaps and priorities</li>
            <li>Goal setting for short-term and long-term objectives</li>
            <li>Resource assessment and budget considerations</li>
          </ul>
        </div>

        <div className="try-this-box">
          <strong>💡 Try This:</strong> "Gene, I've been stuck on finding my 2nd great-grandfather's 
          parents for years. Can you help me develop a new research strategy?"
        </div>

        <div style={{background: '#e3f2fd', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
          <h4 style={{color: '#1565c0', marginBottom: '1rem'}}>Integration with GNology Journal:</h4>
          <p style={{fontSize: '1.05rem', lineHeight: '1.7'}}>
            Use your downloaded GNology Research Journal to document the research strategies Gene suggests. 
            The journal's brick wall analysis forms and research planning templates work perfectly with 
            Gene's guidance to create comprehensive research plans.
          </p>
        </div>
      </TutorialSection>

      <TutorialSection 
        id="generation-counting"
        title="Generation Counting and Family Relationships" 
        icon="👥"
      >
        <p style={{fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '1.5rem'}}>
          Gene provides expert guidance on properly counting generations and understanding family relationships:
        </p>

        <h3 style={{color: '#2e7d32', marginBottom: '1rem'}}>Generation Counting Rules:</h3>
        <ul style={{paddingLeft: '2rem', lineHeight: '1.8', fontSize: '1.05rem', marginBottom: '1.5rem'}}>
          <li>Generation 1: Your earliest known ancestor</li>
          <li>Each parent-to-child relationship = one generational step</li>
          <li>Inclusive counting (both starting and ending persons count)</li>
          <li>Proper handling of "great-grandchildren" relationships (always 3 generations down)</li>
        </ul>

        <div className="try-this-box">
          <strong>💡 Try This:</strong> "Gene, how many generations are there between my 
          great-great-grandfather and his great-grandchildren?"
        </div>

        <div style={{background: '#e8f5e9', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem'}}>
          <h4 style={{color: '#1b5e20', marginBottom: '1rem'}}>Using Generation Counting in Your Journal:</h4>
          <p style={{fontSize: '1.05rem', lineHeight: '1.7'}}>
            The GNology Research Journal includes templates for properly organizing multi-generational 
            family information. Use Gene's generation counting expertise to ensure your journal entries 
            are accurate and systematically organized.
          </p>
        </div>
      </TutorialSection>

      <TutorialSection 
        id="best-practices"
        title="Best Practices for Working with Gene" 
        icon="⭐"
      >
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem'}}>
          <div style={{background: '#e8f5e9', padding: '1.5rem', borderRadius: '8px'}}>
            <h4 style={{color: '#1b5e20', marginBottom: '1rem'}}>Effective Communication:</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li>Be specific about research questions and goals</li>
              <li>Share relevant context about your family</li>
              <li>Ask follow-up questions for clarification</li>
              <li>Provide feedback on helpful suggestions</li>
            </ul>
          </div>

          <div style={{background: '#e3f2fd', padding: '1.5rem', borderRadius: '8px'}}>
            <h4 style={{color: '#1565c0', marginBottom: '1rem'}}>Document Management:</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li>Upload clear, high-quality images</li>
              <li>Include document titles and sources</li>
              <li>Organize questions by family line</li>
              <li>Save Gene's analysis for reference</li>
            </ul>
          </div>

          <div style={{background: '#fff3e0', padding: '1.5rem', borderRadius: '8px'}}>
            <h4 style={{color: '#e65100', marginBottom: '0.8rem'}}>Journal Integration:</h4>
            <ul style={{paddingLeft: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
              <li>Use your downloaded journal alongside Gene conversations</li>
              <li>Fill out journal forms based on Gene's analysis</li>
              <li>Bring journal questions to Gene for guidance</li>
              <li>Track progress using journal templates</li>
            </ul>
          </div>
        </div>

        <div className="try-this-box">
          <strong>💡 Try This:</strong> Before your next session, download the GNology Research Journal, 
          identify your top 5 ancestors, and ask Gene to help prioritize which research goals to tackle first.
        </div>
      </TutorialSection>

      <TutorialSection 
        id="troubleshooting"
        title="Troubleshooting & Common Issues" 
        icon="🛠️"
      >
        <h3 style={{color: '#2e7d32', marginBottom: '1rem'}}>Common Issues & Solutions:</h3>
        
        <div style={{marginBottom: '1.5rem'}}>
          <h4 style={{color: '#d32f2f', marginBottom: '0.5rem'}}>Document won't upload?</h4>
          <p style={{fontSize: '1.05rem', marginLeft: '1rem'}}>
            ✓ Check file size (maximum 200MB)<br/>
            ✓ Ensure file format is supported (PDF, JPG, PNG, DOC, DOCX, TXT)<br/>
            ✓ Try converting to PDF if other formats fail
          </p>
        </div>

        <div style={{marginBottom: '1.5rem'}}>
          <h4 style={{color: '#d32f2f', marginBottom: '0.5rem'}}>PDF Journal won't download?</h4>
          <p style={{fontSize: '1.05rem', marginLeft: '1rem'}}>
            ✓ Check your internet connection<br/>
            ✓ Try right-clicking the download link and "Save As"<br/>
            ✓ Clear browser cache and try again<br/>
            ✓ Visit the Resources page directly for the download link
          </p>
        </div>

        <div style={{marginBottom: '1.5rem'}}>
          <h4 style={{color: '#d32f2f', marginBottom: '0.5rem'}}>Need help organizing research?</h4>
          <p style={{fontSize: '1.05rem', marginLeft: '1rem'}}>
            ✓ Download the GNology Research Journal for structured templates<br/>
            ✓ Focus on 5 ancestors maximum per research project<br/>
            ✓ Use Gene for strategy guidance and document analysis<br/>
            ✓ Visit the Resources page for additional research tools
          </p>
        </div>
      </TutorialSection>

      {/* Remember Section */}
      <div className="remember-section" style={{
        background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
        color: 'white',
        borderRadius: '10px',
        padding: '2rem',
        marginTop: '3rem',
        textAlign: 'center'
      }}>
        <h2 style={{fontSize: '1.8rem', marginBottom: '1rem'}}>
          Remember: Gene is Your Research Partner
        </h2>
        <p style={{fontSize: '1.1rem', lineHeight: '1.8', maxWidth: '800px', margin: '0 auto 1.5rem'}}>
          Gene is designed to be your supportive research companion throughout your genealogical journey. 
          Whether you're just beginning your family history exploration or you're an experienced researcher 
          facing complex challenges, Gene provides the expertise, encouragement, and systematic approach 
          you need to discover your family's unique story.
        </p>
        <p style={{fontSize: '1.1rem', lineHeight: '1.8', maxWidth: '800px', margin: '0 auto 1.5rem'}}>
          The power of Generational Nexus (GNology™) ensures that every interaction with Gene helps build 
          stronger connections between your past, present, and future family narrative. Download your 
          GNology Research Journal and start your systematic research journey today!
        </p>
        <div style={{display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '1rem', marginTop: '1.5rem'}}>
          <button onClick={handleResearchPageClick} style={{
            display: 'inline-block',
            padding: '1rem 2rem',
            background: 'white',
            color: '#2e7d32',
            border: 'none',
            borderRadius: '50px',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '1.1rem',
            boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
            cursor: 'pointer'
          }}>
            {isLoggedIn() ? 'Start Your Conversation with Gene →' : 'Login to Start Your Conversation →'}
          </button>
          <button onClick={handleResourcesPageClick} style={{
            display: 'inline-block',
            padding: '1rem 2rem',
            background: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '50px',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '1.1rem',
            boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
            cursor: 'pointer'
          }}>
            Download GNology Journal →
          </button>
        </div>
      </div>

      {/* Table of Contents Button */}
      <button 
        className="toc-button"
        onClick={() => setShowTOC(true)}
        title="Table of Contents"
      >
        📑
      </button>

      {/* Table of Contents Modal */}
      {showTOC && (
        <div className="toc-modal" onClick={() => setShowTOC(false)}>
          <div className="toc-content" onClick={(e) => e.stopPropagation()}>
            <h2 style={{color: '#2e7d32', marginBottom: '1.5rem'}}>📑 Table of Contents</h2>
            <div className="toc-item" onClick={() => scrollToSection('what-is-gene')}>
              <span style={{fontSize: '1.2rem', marginRight: '0.5rem'}}>🧬</span> What is Gene?
            </div>
            <div className="toc-item" onClick={() => scrollToSection('getting-started')}>
              <span style={{fontSize: '1.2rem', marginRight: '0.5rem'}}>🚀</span> Getting Started
            </div>
            <div className="toc-item" onClick={() => scrollToSection('building-family-tree')}>
              <span style={{fontSize: '1.2rem', marginRight: '0.5rem'}}>🌳</span> Building Your Family Tree
            </div>
            <div className="toc-item" onClick={() => scrollToSection('gnology-research-journal')}>
              <span style={{fontSize: '1.2rem', marginRight: '0.5rem'}}>📄</span> GNology Research Journal
            </div>
            <div className="toc-item" onClick={() => scrollToSection('forensic-genealogy')}>
              <span style={{fontSize: '1.2rem', marginRight: '0.5rem'}}>🏛️</span> Forensic Genealogy with Gene AI
            </div>
            <div className="toc-item" onClick={() => scrollToSection('document-analysis')}>
              <span style={{fontSize: '1.2rem', marginRight: '0.5rem'}}>📄</span> Document Analysis
            </div>
            <div className="toc-item" onClick={() => scrollToSection('document-photo-integration')}>
              <span style={{fontSize: '1.2rem', marginRight: '0.5rem'}}>📸</span> Document & Photo Integration
            </div>
            <div className="toc-item" onClick={() => scrollToSection('family-history-book-writing')}>
              <span style={{fontSize: '1.2rem', marginRight: '0.5rem'}}>📚</span> Family History Book Writing Tutorial
            </div>
            <div className="toc-item" onClick={() => scrollToSection('research-guidance')}>
              <span style={{fontSize: '1.2rem', marginRight: '0.5rem'}}>🔍</span> Research Strategies
            </div>
            <div className="toc-item" onClick={() => scrollToSection('generation-counting')}>
              <span style={{fontSize: '1.2rem', marginRight: '0.5rem'}}>👥</span> Family Relationships
            </div>
            <div className="toc-item" onClick={() => scrollToSection('best-practices')}>
              <span style={{fontSize: '1.2rem', marginRight: '0.5rem'}}>⭐</span> Best Practices
            </div>
            <div className="toc-item" onClick={() => scrollToSection('troubleshooting')}>
              <span style={{fontSize: '1.2rem', marginRight: '0.5rem'}}>🛠️</span> Troubleshooting
            </div>
            <button 
              onClick={() => setShowTOC(false)}
              style={{
                marginTop: '1.5rem',
                padding: '0.75rem 2rem',
                background: '#2e7d32',
                color: 'white',
                border: 'none',
                borderRadius: '50px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600',
                width: '100%'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorialPage;
